import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import Plot from "components/plotly/LocalisedPlot";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useMaterialColor, useMaterialDisplayName } from "hooks/useMaterials";
import { PlotData } from "plotly.js";
import { useMemo } from "react";
import {
  RecipeEditDetail,
  useGetRecipeEditsVisualizationQuery,
} from "src/store/api/generatedApi";

type RecipeEditsModalProps = Readonly<{
  open: boolean;
  onClose: () => void;
  recipeCode: string;
  heatDate: Date;
}>;

export const RecipeEditsModal = ({
  open,
  onClose,
  recipeCode,
}: RecipeEditsModalProps) => {
  const { t } = useTenantTranslation();
  const tenant = useTenant();
  const { getColor } = useMaterialColor();
  const theme = useTheme();

  const { data, isFetching } = useGetRecipeEditsVisualizationQuery(
    {
      tenantName: tenant,
      recipeCode,
    },
    { skip: !open }
  );

  const materialDisplayName = useMaterialDisplayName();

  const plotData = useMemo(() => {
    return getRecipeEditsPlotData(data?.edits, getColor, materialDisplayName);
  }, [data, getColor, materialDisplayName]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          sx: { height: "80vh" },
        },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">
            {t("editingVisualization")}: {recipeCode}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {isFetching ? (
          <Typography>{t("loading")}...</Typography>
        ) : plotData.length > 0 ? (
          <Plot
            data={plotData}
            layout={{
              barmode: "relative",
              xaxis: {
                title: { text: t("basket") },
              },
              yaxis: {
                title: { text: `${t("massChange")} (kg)` },
                zeroline: true,
                zerolinewidth: 2,
                zerolinecolor: theme.palette.text.secondary,
              },
              autosize: true,
              showlegend: true,
              legend: {
                orientation: "v",
                x: 1.02,
                y: 1,
              },
            }}
            config={{
              responsive: true,
              displayModeBar: true,
              displaylogo: false,
            }}
            style={{ width: "100%", height: "calc(80vh - 120px)" }}
            useResizeHandler
          />
        ) : (
          <Typography>{t("noEditingDataAvailable")}</Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

const getRecipeEditsPlotData = (
  edits: RecipeEditDetail[] | undefined,
  getColor: (materialName: string) => string,
  materialDisplayName: (materialName: string) => string
): Partial<PlotData>[] => {
  if (!edits || edits.length === 0) return [];

  const basketEdits = new Map<
    number,
    {
      increases: {
        material: string;
        value: number;
        basketLevelChange: boolean;
        recipeLevelChange: boolean;
      }[];
      decreases: {
        material: string;
        value: number;
        basketLevelChange: boolean;
        recipeLevelChange: boolean;
      }[];
    }
  >();

  for (const edit of edits) {
    const basket = edit.deployed_basket ?? edit.suggested_basket;
    if (basket === null) continue;

    if (!basketEdits.has(basket)) {
      basketEdits.set(basket, { increases: [], decreases: [] });
    }

    const basketData = basketEdits.get(basket);
    if (!basketData) continue;
    const basketLevelChange =
      edit.edit_type === "added" || edit.edit_type === "removed";
    const recipeLevelChange =
      edit.heat_level_edit_type === "added" ||
      edit.heat_level_edit_type === "removed";

    if (edit.mass_difference > 0) {
      basketData.increases.push({
        material: edit.material_name,
        value: edit.mass_difference,
        basketLevelChange,
        recipeLevelChange,
      });
    } else if (edit.mass_difference < 0) {
      basketData.decreases.push({
        material: edit.material_name,
        value: Math.abs(edit.mass_difference),
        basketLevelChange,
        recipeLevelChange,
      });
    }
  }

  const sortedBaskets = Array.from(basketEdits.keys()).sort((a, b) => a - b);

  const traces: Partial<PlotData>[] = [];
  const materialsInLegend = new Set<string>();

  for (const basket of sortedBaskets) {
    const basketEditData = basketEdits.get(basket);
    if (!basketEditData) continue;
    const { increases, decreases } = basketEditData;

    const createTrace = (items: typeof increases, isIncrease: boolean) => {
      for (const {
        material,
        value,
        basketLevelChange,
        recipeLevelChange,
      } of items) {
        const showInLegend = !materialsInLegend.has(material);
        if (showInLegend) {
          materialsInLegend.add(material);
        }

        traces.push({
          x: [`Basket ${basket}`],
          y: [isIncrease ? value : -value],
          name: materialDisplayName(material),
          type: "bar",
          legendgroup: material,
          showlegend: showInLegend,
          marker: {
            color: getColor(material),
            pattern: recipeLevelChange
              ? { shape: "+" }
              : basketLevelChange
                ? { shape: isIncrease ? "/" : "\\" }
                : undefined,
          },
          hovertemplate: `${materialDisplayName(material)}: ${
            isIncrease ? "+" : ""
          }%{y:.1f} kg<extra></extra>`,
        });
      }
    };

    createTrace(increases, true);
    createTrace(decreases, false);
  }

  return traces;
};
